import type { Role } from "@/store/auth-store";
import type { Permission } from "@/api/admin-api";
import {
  formatDateDMY,
  formatDateTimeDMY,
  formatDateTimeDMYSeconds,
} from "@/utils/dateFormat";

export interface RoleMeta {
  label: string;
  badge: string;
  ring: string;
  description: string;
}

// Tailwind classes are spelled out as literal strings so the JIT picks
// them up. Avoid template-stitching utility names — Tailwind cannot see
// them at build time.
export const ROLE_META: Record<Role, RoleMeta> = {
  TESTRAD: {
    label: "Candidate",
    badge: "bg-status-new-bg text-status-new",
    ring: "ring-status-new",
    description: "A job applicant sitting a reporting assessment.",
  },
  RADIOLOGIST: {
    label: "Radiologist",
    badge: "bg-badge-radiologist-bg text-badge-radiologist",
    ring: "ring-badge-radiologist",
    description: "Builds the exams, reviews recordings and scores them.",
  },
  ADMIN: {
    label: "Admin",
    badge: "bg-badge-admin-bg text-badge-admin",
    ring: "ring-badge-admin",
    description: "Accounts, settings, the case library and the audit log.",
  },
  SUPER_ADMIN: {
    label: "Super Admin",
    badge: "bg-badge-admin-bg text-badge-admin border border-border-accent",
    ring: "ring-border-accent",
    description: "Full access, including the per-candidate purge.",
  },
};

// Re-exported from usePermissions, where it is DERIVED from the
// ``Record<Role, ...>`` permission matrix.
//
// It used to be a hand-written array here. A plain array does not fail to
// compile when a role is added, so it was a silent drift hazard -- and its
// sibling ALL_PERMISSIONS below had already drifted, missing
// can_manage_organisation for who knows how long. Deriving both makes
// TypeScript the enforcement instead of vigilance.
export { ALL_ROLES } from "@/hooks/usePermissions";

// SUPER_ADMIN is created out-of-band (seed script / DB), never via the
// admin UI, so it is excluded from the role-card grid in tab 2.
export const CREATABLE_ROLES: Role[] = ["TESTRAD", "RADIOLOGIST", "ADMIN"];

// Keyed as a Record so the compiler REQUIRES a label for every member of
// ``Permission``. The previous shape was an array, which is why
// can_manage_organisation could go missing without anyone noticing.
const PERMISSION_LABELS: Record<Permission, string> = {
  can_manage_users: "Manage users",
  can_manage_organisation: "Manage settings",
  can_take_test: "Sit an assessment",
  can_manage_tests: "Build and review assessments",
  can_upload_test_cases: "Upload assessment cases",
};

export const ALL_PERMISSIONS: { key: Permission; label: string }[] = (
  Object.keys(PERMISSION_LABELS) as Permission[]
).map((key) => ({ key, label: PERMISSION_LABELS[key] }));

// Action -> tone for the audit log filter pills.
//
// "deploy" is its own tone rather than reusing neutral: a code change to
// the whole system is not the same kind of event as the routine rows it
// sits between, and at neutral it was the same grey as every worksheet
// open. It is not "success" either — a DEPLOYMENT_FAILED row is a
// deployment too, and it takes the danger tone below.
export type AuditTone = "success" | "danger" | "neutral" | "deploy";

export function auditTone(action: string): AuditTone {
  if (action === "LOGIN_SUCCESS") return "success";
  if (action === "DEPLOYMENT_APPLIED") return "deploy";
  if (
    action === "LOGIN_FAILED" ||
    action === "REFRESH_REPLAY" ||
    action === "REPORT_SIGN_DENIED" ||
    // A transcription that never came back is a lost dictation — the
    // sonographer spoke and nothing arrived. It should read as a failure
    // in the log, not sit neutral among the routine rows.
    action === "DICTATION_FAILED" ||
    action === "AGENT_RETRIEVAL_ALARM_RAISED" ||
    // A deployment that did not come up clean left the box part-way:
    // the repo is at the new commit while the containers may still be
    // running the old code. That reads as a failure, not as an update.
    action === "DEPLOYMENT_FAILED" ||
    // Purging a candidate destroys their recordings, and nothing else in
    // the system ever deletes one — assessment audio is deliberately
    // exempt from the retention sweep. It is irreversible and it should
    // not sit grey among the routine rows.
    action === "TEST_CANDIDATE_PURGED"
  ) {
    return "danger";
  }
  return "neutral";
}

// ---------------------------------------------------------------------
// Password strength
// ---------------------------------------------------------------------

export type StrengthBand = "weak" | "fair" | "good" | "strong";

export interface StrengthResult {
  score: number; // 0..4
  band: StrengthBand;
  label: string;
  color: string; // tailwind class for the bar
  pillTone: string; // tailwind class for the label pill
}

/** Pragmatic strength heuristic. Backend min is 10 chars per
 * ``AdminUserCreateIn``; we mirror that floor here so a "Strong" rating
 * cannot be reached on a sub-10-char password. */
export function passwordStrength(pw: string): StrengthResult {
  if (!pw) {
    return {
      score: 0,
      band: "weak",
      label: "Weak",
      color: "bg-status-urgent",
      pillTone: "bg-status-urgent-bg text-status-urgent",
    };
  }

  let score = 0;
  if (pw.length >= 10) score += 1;
  if (pw.length >= 14) score += 1;
  const classes =
    Number(/[a-z]/.test(pw)) +
    Number(/[A-Z]/.test(pw)) +
    Number(/[0-9]/.test(pw)) +
    Number(/[^A-Za-z0-9]/.test(pw));
  if (classes >= 3) score += 1;
  if (classes === 4 && pw.length >= 12) score += 1;

  if (score <= 1) {
    return {
      score,
      band: "weak",
      label: "Weak",
      color: "bg-status-urgent",
      pillTone: "bg-status-urgent-bg text-status-urgent",
    };
  }
  if (score === 2) {
    return {
      score,
      band: "fair",
      label: "Fair",
      color: "bg-status-wip",
      pillTone: "bg-status-wip-bg text-status-wip",
    };
  }
  if (score === 3) {
    return {
      score,
      band: "good",
      label: "Good",
      color: "bg-accent",
      pillTone: "bg-accent-light text-accent",
    };
  }
  return {
    score,
    band: "strong",
    label: "Strong",
    color: "bg-status-done",
    pillTone: "bg-status-done-bg text-status-done",
  };
}

// ---------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------

export function formatRelativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const now = Date.now();
  const diff = now - t;
  if (diff < 0) return formatDateTimeDMY(iso);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateDMY(iso);
}

export function formatTimestamp(iso: string): string {
  return formatDateTimeDMYSeconds(iso);
}
